import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Calendar,
  ClipboardCheck,
  Eye,
  Globe,
  Loader2,
  Lock,
  Mail,
  MapPin,
  MessageSquare,
  Rocket,
  Search,
  Sparkles,
  Upload,
  Users,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { buildListingFilterParams, type ListingFilterValues } from "@/components/ListingFilters";
import { Pagination } from "@/components/ui/pagination";
import Loader from "@/components/ui/loader";
import { useToast } from "@/components/ui/toast";
import { useAuth } from "@/contexts/AuthContext";
import { isFreeBuyerUser } from "@/lib/access";
import { ADMIN_ROLES, isOrgAdmin } from "@/lib/roles";

/* Coming-soon deals browser — the "Coming Soon" tab of the buyer marketplace.
   Same /lead-pipeline data as the Deal Pipeline page, but embedded next to the
   active listings so buyers browse both from one place. Staff (BL advisors and
   admins) can publish a lead as a listing from here too — gated behind the same
   pre-publish checklist as the pipeline pages. */

interface LeadPipeline {
  id: string;
  org_id?: number;
  reference_code: string | null;
  image_url: string | null;
  title: string | null;
  company_name: string | null;
  owner_email: string | null;
  website_url: string | null;
  business_description: string | null;
  industry_category: string | null;
  location: string | null;
  potential_category: string | null;
  interest_count?: number;
  created_at?: string;
}

interface LeadPipelinePage {
  total_elements: number;
  page: number;
  page_size: number;
  data: LeadPipeline[];
}

/** Full lead payload from /lead-pipeline/:id — the shape CreateListing's
    leadPrefill expects (industry/location ids the list payload lacks). */
interface LeadDetail extends LeadPipeline {
  industry_category_id: number | null;
  country_id: number | null;
  state_id: number | null;
  city_id: number | null;
  city_raw: string | null;
}

const leadTitle = (l: LeadPipeline) => l.title || "Off-market opportunity";

const ComingSoonBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-[linear-gradient(135deg,hsl(43_74%_60%),hsl(38_64%_46%))] px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[hsl(40_45%_12%)] shadow-sm whitespace-nowrap">
    <Sparkles className="h-3 w-3" />
    Coming Soon
  </span>
);

// Remember the page for this session so leaving the marketplace (a deal
// detail, /choose-plan, a tab switch) and coming back lands where the buyer
// was, instead of snapping to page 1. Mirrors LISTINGS_PAGE_KEY in Home.tsx,
// which does the same for the Active Listings tab.
const COMING_SOON_PAGE_KEY = "buyerComingSoonPage";

function readSavedComingSoonPage(): number {
  try {
    const v = Number(sessionStorage.getItem(COMING_SOON_PAGE_KEY));
    return Number.isFinite(v) && v >= 1 ? v : 1;
  } catch {
    return 1;
  }
}

export function ComingSoonDeals({
  filters,
  viewMode,
  pageSize,
  onTotalChange,
}: {
  /** Applied marketplace filters — the shared search/filter bar drives this tab too. */
  filters: ListingFilterValues;
  viewMode: "grid" | "table";
  pageSize: number;
  /** Reports the UNFILTERED total so the parent can badge the tab — filtered
      fetches don't call this, keeping the badge stable while searching. */
  onTotalChange?: (n: number) => void;
}) {
  const { user, hasRole } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isStaff = hasRole("bl_advisor", ...ADMIN_ROLES);
  // Mirrors the API's lead scope (admin.org_scope): default-org staff may
  // publish any lead, an org_admin or a tenant advisor only their own org's.
  // Without this the button led to a filled-in form the API then refused.
  const canPublish = (l: LeadPipeline) =>
    isStaff && ((!isOrgAdmin(user) && (user?.org_id ?? 1) === 1) || l.org_id === user?.org_id);
  const isFreeBuyer = isFreeBuyerUser(user);

  const [leads, setLeads] = useState<LeadPipeline[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(readSavedComingSoonPage);
  // Page for the very first fetch (restored from the session). After that only
  // a real filter/page-size change resets to page 1.
  const initialPageRef = useRef(readSavedComingSoonPage());
  const didInitialFetchRef = useRef(false);
  const lastBrowseSigRef = useRef<string>("");
  const [loading, setLoading] = useState(true);
  const [detailModal, setDetailModal] = useState<LeadPipeline | null>(null);
  const [interestModal, setInterestModal] = useState<LeadPipeline | null>(null);
  const [publishModal, setPublishModal] = useState<LeadPipeline | null>(null);
  const [publishChecklist, setPublishChecklist] = useState<{
    owner_connected: boolean | null;
    due_diligence_completed: boolean | null;
    nda_signed: boolean | null;
  }>({ owner_connected: null, due_diligence_completed: null, nda_signed: null });
  const [publishNotes, setPublishNotes] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<Set<string>>(new Set());
  const [generatingImageIds, setGeneratingImageIds] = useState<Set<string>>(new Set());
  const imageAttemptedRef = useRef<Set<string>>(new Set());

  const fetchLeads = useCallback(async (targetPage = 1) => {
    setLoading(true);
    setCurrentPage(targetPage);
    try {
      const params = buildListingFilterParams(filters);
      params.set("page", String(targetPage));
      params.set("page_size", String(pageSize));
      const data = await api.get<LeadPipelinePage>(`/lead-pipeline?${params}`);
      setLeads(data.data);
      setTotal(data.total_elements);
      // Only an unfiltered fetch represents the true pipeline total; filtered
      // results must not shrink the tab badge.
      const isFiltered = Boolean(
        // SCRUM-90: multi-location filter — location filters are now arrays.
        filters.search || filters.categoryIds.length || filters.countryIds.length || filters.stateIds.length || filters.cityIds.length,
      );
      if (!isFiltered) onTotalChange?.(data.total_elements);
    } catch {
      setLeads([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, onTotalChange]);

  useEffect(() => {
    try {
      sessionStorage.setItem(COMING_SOON_PAGE_KEY, String(currentPage));
    } catch {
      /* ignore storage errors */
    }
  }, [currentPage]);

  // Keying off a filter *signature* rather than the fetchLeads identity is what
  // keeps a remount (or a re-memoised fetcher) from bouncing the restored page
  // back to 1 — see the same guard in Home.tsx.
  const browseSig = JSON.stringify({ f: filters, p: pageSize });
  useEffect(() => {
    if (!didInitialFetchRef.current) {
      didInitialFetchRef.current = true;
      lastBrowseSigRef.current = browseSig;
      fetchLeads(initialPageRef.current);
      return;
    }
    if (lastBrowseSigRef.current !== browseSig) {
      lastBrowseSigRef.current = browseSig;
      fetchLeads(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseSig]);

  // Lazily generate + persist a cover image for any visible lead that lacks
  // one (same behavior as the Deal Pipeline page). One at a time; saved images
  // are reused on later views.
  useEffect(() => {
    // /generate-image is subscription-gated; free (Basic) buyers would fire a
    // 402 per card. They keep the placeholder cover instead.
    if (isFreeBuyer) return;
    const missing = leads.filter((l) => !l.image_url && !imageAttemptedRef.current.has(l.id));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const lead of missing) {
        if (cancelled) return;
        imageAttemptedRef.current.add(lead.id);
        setGeneratingImageIds((prev) => new Set(prev).add(lead.id));
        try {
          const updated = await api.post<LeadPipeline>(`/lead-pipeline/${lead.id}/generate-image`, {});
          if (!cancelled && updated.image_url) {
            setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, image_url: updated.image_url } : l)));
          }
        } catch {
          /* leave placeholder; don't retry this id */
        } finally {
          setGeneratingImageIds((prev) => {
            const next = new Set(prev);
            next.delete(lead.id);
            return next;
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [leads]);

  const handleInterest = async () => {
    if (!interestModal) return;
    setSubmitting(true);
    try {
      await api.post(`/lead-pipeline/${interestModal.id}/interest`, {
        buyer_message: message.trim() || null,
      });
      setSubmitted((prev) => new Set([...prev, interestModal.id]));
      setInterestModal(null);
      setMessage("");
      toast.success("Interest submitted! An advisor will be in touch soon.");
    } catch (e: any) {
      if (e?.status === 409) {
        toast.error("You've already expressed interest in this lead.");
        setSubmitted((prev) => new Set([...prev, interestModal.id]));
        setInterestModal(null);
      } else {
        toast.error(e instanceof Error ? e.message : "Failed to submit");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Coming-soon deals are members-only: free (Basic) buyers get sent to plan
  // selection instead of a modal the /lead-pipeline API would reject with a 402.
  const openDetail = (lead: LeadPipeline) => {
    if (isFreeBuyer) {
      navigate("/choose-plan");
      return;
    }
    setDetailModal(lead);
  };

  const openInterest = (lead: LeadPipeline) => {
    if (isFreeBuyer) {
      navigate("/choose-plan");
      return;
    }
    setInterestModal(lead);
    setMessage("");
  };

  const openPublish = (lead: LeadPipeline) => {
    setPublishChecklist({ owner_connected: null, due_diligence_completed: null, nda_signed: null });
    setPublishNotes("");
    setDetailModal(null);
    setPublishModal(lead);
  };

  const allChecklistYes =
    publishChecklist.owner_connected === true &&
    publishChecklist.due_diligence_completed === true &&
    publishChecklist.nda_signed === true;

  // Fetch the full lead (industry/location ids) then hand off to the Create
  // Listing form — the same flow as the pipeline pages' Publish as Listing.
  const confirmPublish = async () => {
    if (!publishModal || !allChecklistYes) {
      toast.error("Please confirm all three checklist items before publishing.");
      return;
    }
    setPublishing(true);
    try {
      const lead = await api.get<LeadDetail>(`/lead-pipeline/${publishModal.id}`);
      navigate("/listings/new", {
        state: {
          leadPrefill: lead,
          advisorChecklist: {
            owner_connected: true,
            due_diligence_completed: true,
            nda_signed: true,
            notes: publishNotes.trim() || null,
          },
        },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load lead details");
      setPublishing(false);
    }
  };

  const hasActiveFilters = Boolean(
    // SCRUM-90: multi-location filter — location filters are now arrays.
    filters.search || filters.categoryIds.length || filters.countryIds.length || filters.stateIds.length || filters.cityIds.length,
  );

  /* Same cover treatment as the active listing cards on Home: dark gold-tinted
     placeholder, hatch pattern, and a top/bottom scrim over real photos. */
  const coverImage = (l: LeadPipeline, heightClass: string, iconClass: string) => (
    <div className={`relative ${heightClass} w-full shrink-0 overflow-hidden bg-[linear-gradient(135deg,hsl(40_20%_14%),hsl(40_16%_9%))]`}>
      {l.image_url ? (
        <>
          <img src={l.image_url} alt={leadTitle(l)} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/25" />
        </>
      ) : generatingImageIds.has(l.id) ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-white/50">
          <Loader2 className="h-7 w-7 animate-spin" />
          <span className="text-[0.65rem] font-semibold uppercase tracking-wide">Generating image…</span>
        </div>
      ) : (
        <>
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{ backgroundImage: "repeating-linear-gradient(135deg, hsl(43 30% 60% / 0.08) 0 10px, transparent 10px 20px)" }}
          />
          <div className="relative flex h-full w-full items-center justify-center">
            <Building2 className={`${iconClass} text-white/25`} />
          </div>
        </>
      )}
    </div>
  );

  if (loading && leads.length === 0) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <div key={item} className="rounded-xl border border-border/80 bg-card p-5 shadow-card">
            <div className="h-5 w-3/4 skeleton" />
            <div className="mt-3 h-4 w-full skeleton" />
            <div className="mt-2 h-4 w-2/3 skeleton" />
            <div className="mt-6 h-10 w-full skeleton" />
          </div>
        ))}
      </div>
    );
  }

  if (leads.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border/80 bg-card px-6 py-12 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-muted/60 text-muted-foreground/60">
          {hasActiveFilters ? <Search className="h-7 w-7" /> : <Rocket className="h-7 w-7" />}
        </div>
        <h3 className="mt-4 text-xl font-bold text-foreground">
          {hasActiveFilters ? "No coming-soon deals match your filters" : "Fresh deals are on the way"}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {hasActiveFilters
            ? "Try broadening your search, category, or location using the filters above."
            : "We're lining up exclusive off-market opportunities. Check back soon — they'll appear here first."}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/60 backdrop-blur-sm">
            <Loader size="sm" />
          </div>
        )}

        {viewMode === "grid" ? (
          <div className={`grid gap-4 md:grid-cols-2 xl:grid-cols-3 transition-opacity duration-200 ${loading ? "pointer-events-none opacity-40" : "opacity-100"}`}>
            {leads.map((l) => {
              const isSubmitted = submitted.has(l.id);
              return (
                <div
                  key={l.id}
                  className="hover-lift public-listing-card group flex flex-col overflow-hidden rounded-2xl border border-border/70 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="relative">
                    {coverImage(l, "h-44", "h-12 w-12")}
                    {(l.industry_category || l.potential_category) && (
                      <div className="absolute left-3 top-3 flex flex-col items-start gap-1">
                        {/* Dark glass over the photo in BOTH themes, same as the
                            active listing cards — so white, not foreground. */}
                        {l.industry_category && (
                          <span className="inline-flex items-center rounded-md border border-primary/25 bg-black/60 px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-white/90 backdrop-blur-sm">
                            {l.industry_category}
                          </span>
                        )}
                        {l.potential_category && (
                          <span className="inline-flex items-center rounded-md border border-violet-300/30 bg-black/60 px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-violet-200 backdrop-blur-sm">
                            {l.potential_category}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="absolute right-3 top-3">
                      <ComingSoonBadge />
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div>
                      {/* Reference code above the title, with the row holding its
                          height when a lead has none, so titles line up. */}
                      <div className="mb-1 flex h-4 items-center">
                        {l.reference_code && (
                          <span className="font-mono text-xs font-bold tracking-wider text-primary">
                            {l.reference_code}
                          </span>
                        )}
                      </div>
                      <div className="min-h-[4.15rem]">
                        <h3 className="line-clamp-2 font-display text-xl font-bold leading-snug text-foreground">
                          {leadTitle(l)}
                        </h3>
                      </div>
                      {l.business_description && (
                        <p className="mt-1 line-clamp-3 text-xs leading-5 text-muted-foreground">{l.business_description}</p>
                      )}
                    </div>

                    {l.location && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                        <span className="truncate" title={l.location}>{l.location}</span>
                      </div>
                    )}

                    {!isStaff && !isSubmitted && (
                      <div className="flex items-center gap-1.5 text-[0.7rem] text-muted-foreground">
                        <Lock className="h-3 w-3 shrink-0" />
                        Express interest to unlock full details
                      </div>
                    )}

                    {/* Button row mirrors the active listing cards: outline gold
                        secondary + gold-gradient primary, both full width. */}
                    <div className={`mt-auto grid gap-2 pt-1 ${isStaff ? "grid-cols-1" : "grid-cols-2"}`}>
                      <button
                        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-transparent py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
                        onClick={() => openDetail(l)}
                      >
                        <Eye className="h-4 w-4" />
                        View Details
                      </button>
                      {canPublish(l) && (
                        <button
                          className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-gold-gradient py-2.5 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-[1.06]"
                          onClick={() => openPublish(l)}
                        >
                          <Upload className="h-4 w-4" />
                          Publish as Listing
                        </button>
                      )}
                      {!isStaff && (isSubmitted ? (
                        <div className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-emerald-500/25 bg-emerald-500/10 py-2.5 text-sm font-semibold text-emerald-400">
                          <Sparkles className="h-4 w-4" />
                          Interest sent
                        </div>
                      ) : (
                        <button
                          className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-gold-gradient py-2.5 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-[1.06]"
                          onClick={() => openInterest(l)}
                        >
                          <Sparkles className="h-4 w-4" />
                          Express Interest
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={`overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm transition-opacity duration-200 ${loading ? "pointer-events-none opacity-40" : "opacity-100"}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/70 bg-muted/40">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">BL Code</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {leads.map((l) => {
                    const isSubmitted = submitted.has(l.id);
                    return (
                      <tr key={l.id} className="group transition-colors hover:bg-muted/30">
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          {l.reference_code ? (
                            <span className="font-mono text-xs font-bold tracking-wider text-primary">
                              {l.reference_code}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/50">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          {l.industry_category ? (
                            <span className="inline-flex items-center rounded-full border border-primary/15 bg-primary/10 px-2 py-0.5 text-[0.64rem] font-semibold uppercase tracking-[0.1em] text-primary whitespace-nowrap">
                              {l.industry_category}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/50">—</span>
                          )}
                        </td>
                        <td className="max-w-[280px] px-4 py-3.5">
                          <span className="font-semibold text-foreground line-clamp-1">{leadTitle(l)}</span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          {l.location ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5 shrink-0" />
                              {l.location}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/50">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5">
                          <ComingSoonBadge />
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="outline" className="whitespace-nowrap border-primary/15 bg-white/72 hover:bg-white" onClick={() => openDetail(l)}>
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </Button>
                            {canPublish(l) && (
                              <Button size="sm" className="whitespace-nowrap" onClick={() => openPublish(l)}>
                                <Upload className="h-3.5 w-3.5" />
                                Publish
                              </Button>
                            )}
                            {!isStaff && (isSubmitted ? (
                              <span className="whitespace-nowrap rounded-lg bg-green-50 px-2.5 py-1.5 text-xs font-semibold text-green-700">✓ Submitted</span>
                            ) : (
                              <Button size="sm" className="whitespace-nowrap" onClick={() => openInterest(l)}>
                                <Sparkles className="h-3.5 w-3.5" />
                                Interest
                              </Button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && total > pageSize && (
        <Pagination
          page={currentPage}
          totalElements={total}
          pageSize={pageSize}
          onChange={fetchLeads}
          className="mt-6"
        />
      )}

      {/* Lead details modal (read-only; owner details stay locked for buyers) */}
      {detailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setDetailModal(null)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div
            className="relative max-h-[90dvh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              {coverImage(detailModal, "h-48", "h-14 w-14")}
              <div className="absolute right-3 top-3">
                <button
                  onClick={() => setDetailModal(null)}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-muted-foreground shadow-sm backdrop-blur-sm hover:bg-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                {detailModal.industry_category && (
                  <span className="inline-flex items-center rounded-full border border-primary/20 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                    {detailModal.industry_category}
                  </span>
                )}
                {detailModal.potential_category && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-violet-700 backdrop-blur-sm">
                    {detailModal.potential_category}
                  </span>
                )}
              </div>
            </div>

            <div className="max-h-[calc(90vh-12rem)] overflow-y-auto p-6">
              <h2 className="text-xl font-extrabold leading-snug text-gray-900">{leadTitle(detailModal)}</h2>
              {detailModal.reference_code && (
                <p className="mt-1 font-mono text-xs tracking-wide text-gray-400">{detailModal.reference_code}</p>
              )}
              {detailModal.location && (
                <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
                  <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                  <span>{detailModal.location}</span>
                </div>
              )}

              {detailModal.business_description && (
                <div className="mt-4">
                  <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Description</p>
                  <p className="mt-1.5 whitespace-pre-line text-sm leading-6 text-gray-600">{detailModal.business_description}</p>
                </div>
              )}

              {isStaff ? (
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {detailModal.company_name && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-border/70 px-3 py-2.5">
                      <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                      <div className="min-w-0">
                        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Company</p>
                        <p className="truncate text-sm text-foreground" title={detailModal.company_name}>{detailModal.company_name}</p>
                      </div>
                    </div>
                  )}
                  {detailModal.owner_email && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-border/70 px-3 py-2.5">
                      <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                      <div className="min-w-0">
                        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Owner email</p>
                        <a href={`mailto:${detailModal.owner_email}`} className="block truncate text-sm text-primary hover:underline" title={detailModal.owner_email}>{detailModal.owner_email}</a>
                      </div>
                    </div>
                  )}
                  {detailModal.website_url && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-border/70 px-3 py-2.5">
                      <Globe className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                      <div className="min-w-0">
                        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Website</p>
                        <a href={detailModal.website_url} target="_blank" rel="noopener noreferrer" className="block truncate text-sm text-primary hover:underline" title={detailModal.website_url}>{detailModal.website_url}</a>
                      </div>
                    </div>
                  )}
                  {typeof detailModal.interest_count === "number" && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-border/70 px-3 py-2.5">
                      <Users className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                      <div className="min-w-0">
                        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Buyer interest</p>
                        <p className="text-sm text-foreground">{detailModal.interest_count} {detailModal.interest_count === 1 ? "buyer" : "buyers"}</p>
                      </div>
                    </div>
                  )}
                  {detailModal.created_at && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-border/70 px-3 py-2.5">
                      <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                      <div className="min-w-0">
                        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Added</p>
                        <p className="text-sm text-foreground">{new Date(detailModal.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-5 flex items-center gap-2 rounded-xl border border-primary/15 bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
                  <Lock className="h-4 w-4 shrink-0 text-primary/70" />
                  Express interest to unlock the owner contact and full company details.
                </div>
              )}

              <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDetailModal(null)}>
                  Close
                </Button>
                {canPublish(detailModal) && (
                  <Button onClick={() => openPublish(detailModal)}>
                    <Upload className="mr-1 h-4 w-4" />
                    Publish as Listing
                  </Button>
                )}
                {!isStaff && (submitted.has(detailModal.id) ? (
                  <div className="rounded-xl bg-green-50 px-4 py-2 text-sm font-semibold text-green-700">✓ Interest Submitted</div>
                ) : (
                  <Button
                    onClick={() => {
                      const lead = detailModal;
                      setDetailModal(null);
                      openInterest(lead);
                    }}
                  >
                    <Sparkles className="mr-1 h-4 w-4" />
                    Express Interest
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pre-publish checklist modal (staff) — same gate as the pipeline pages */}
      {publishModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setPublishModal(null)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-primary" />
                <h2 className="text-lg font-bold text-foreground">Pre-Publish Checklist</h2>
              </div>
              <button onClick={() => setPublishModal(null)}>
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            <div className="mb-4 rounded-xl border border-primary/15 bg-primary/5 p-3">
              <p className="text-sm font-semibold text-foreground">{leadTitle(publishModal)}</p>
              {publishModal.reference_code && (
                <p className="mt-0.5 font-mono text-[0.65rem] tracking-wide text-muted-foreground">{publishModal.reference_code}</p>
              )}
            </div>
            <div className="space-y-2.5">
              {([
                { key: "owner_connected", label: "Have you connected with the owner?" },
                { key: "due_diligence_completed", label: "Have you completed all due diligence?" },
                { key: "nda_signed", label: "Is the NDA signed with the owner?" },
              ] as const).map((q) => (
                <div key={q.key} className="flex items-center justify-between gap-3 rounded-xl border border-border/70 px-3 py-2.5">
                  <span className="text-xs text-foreground">{q.label}</span>
                  <div className="flex shrink-0 gap-1.5">
                    {([{ val: true, text: "Yes" }, { val: false, text: "No" }] as const).map((opt) => {
                      const active = publishChecklist[q.key] === opt.val;
                      return (
                        <button
                          key={opt.text}
                          type="button"
                          onClick={() => setPublishChecklist((p) => ({ ...p, [q.key]: opt.val }))}
                          className={`rounded-lg px-3 py-1 text-xs font-semibold transition-colors ${
                            active
                              ? opt.val ? "bg-green-600 text-white" : "bg-rose-600 text-white"
                              : "bg-muted text-muted-foreground hover:bg-muted/70"
                          }`}
                        >
                          {opt.text}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {!allChecklistYes && (
              <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600">
                All three items must be confirmed <span className="font-bold">Yes</span> before you can publish.
              </p>
            )}
            <div className="mt-4">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Notes (optional)</label>
              <textarea
                rows={2}
                className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={publishNotes}
                onChange={(e) => setPublishNotes(e.target.value)}
                placeholder="Anything worth recording about this publish…"
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPublishModal(null)} disabled={publishing}>
                Cancel
              </Button>
              <Button onClick={confirmPublish} disabled={!allChecklistYes || publishing}>
                {publishing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
                Publish as Listing
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Express-interest modal */}
      {interestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setInterestModal(null)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">Express Interest</h2>
              <button onClick={() => setInterestModal(null)}>
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            <div className="mb-4 rounded-xl border border-primary/15 bg-primary/5 p-3">
              <p className="text-sm font-semibold text-foreground">{leadTitle(interestModal)}</p>
              <div className="mt-1 flex gap-3 text-xs text-muted-foreground">
                {interestModal.industry_category && <span>{interestModal.industry_category}</span>}
                {interestModal.potential_category && <span>{interestModal.potential_category}</span>}
              </div>
            </div>
            <div className="mb-4">
              <label className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
                <MessageSquare className="h-3.5 w-3.5" /> Message (optional)
              </label>
              <textarea
                rows={3}
                className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us about your interest, timeline, or any questions..."
              />
            </div>
            <p className="mb-4 text-xs text-muted-foreground">
              Once submitted, a BL Advisor will be assigned and will reach out to you directly.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setInterestModal(null)}>
                Cancel
              </Button>
              <Button onClick={handleInterest} disabled={submitting}>
                {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
